import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation, useCurrentLanguage } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'

const asArray = (value) => (Array.isArray(value) ? value : [])

/**
 * @param {{ document: 'privacy' | 'terms' }} props
 */
export default function SmsLegalDocument({ document }) {
  const t = useTranslation()
  const { isLoading } = useLanguage()
  const language = useCurrentLanguage()
  const location = useLocation()
  const prefix = language === 'pt' ? '/pt' : language === 'en' ? '/en' : ''

  const baseKey = `smsLegal.${document}`
  const metaTitle = t(`${baseKey}.meta.title`)
  const metaDescription = t(`${baseKey}.meta.description`)
  const sections = asArray(t(`${baseKey}.sections`))
  const relatedPath = t(
    document === 'privacy' ? `${baseKey}.relatedTermsPath` : `${baseKey}.relatedPrivacyPath`,
    document === 'privacy' ? '/sms-terms' : '/sms-privacy-policy'
  )
  const relatedLabel = t(
    document === 'privacy' ? `${baseKey}.relatedTermsLabel` : `${baseKey}.relatedPrivacyLabel`,
    document === 'privacy' ? 'SMS Terms' : 'SMS Privacy Policy'
  )

  usePageSeo({
    enabled: !isLoading,
    title: `${metaTitle} | VIVA Psychotherapy`,
    description: metaDescription,
    pathname: location.pathname,
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading…</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <header className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            {t(`${baseKey}.pageTitle`)}
          </h1>
          <p className="text-sm text-gray-500">
            {language === 'pt' ? 'Última atualização: ' : 'Last updated: '}
            {t(`${baseKey}.lastUpdated`)}
          </p>
          <p className="mt-6 text-base sm:text-lg text-gray-700 leading-relaxed">
            {t(`${baseKey}.intro`)}
          </p>
        </header>

        <div className="space-y-8 text-gray-700">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">{section.title}</h2>
              {asArray(section.paragraphs).map((paragraph) => (
                <p key={paragraph.slice(0, 48)} className="mb-3 leading-relaxed text-base">
                  {paragraph}
                </p>
              ))}
            </section>
          ))}
        </div>

        <footer className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap gap-4 text-sm">
          <Link
            to={`${prefix}${relatedPath}`}
            className="text-primary-darkGreen font-medium hover:underline"
          >
            {relatedLabel}
          </Link>
          <Link
            to={language === 'pt' ? '/pt/contact' : '/contact'}
            className="text-gray-600 hover:text-primary-darkGreen hover:underline"
          >
            {t('common.nav.contact')}
          </Link>
        </footer>
      </article>
    </div>
  )
}
